import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import {
  UpdateLicense,
  GetStoreProducts,
} from "../../api"
import { navigate } from "vike/client/router"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"

export default function DetailsTab({ license, setLicense, refreshKey, setRefreshKey, onClose }) {
  const [productOptions, setProductOptions] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)


  // Load store products
  const loadStoreProducts = async (search = "") => {
    try {
      setLoadingProducts(true)
      const response = await GetStoreProducts({
        search,
        itemsPerPage: -1,
      })

      if (response.result && response.data) {
        const options = response.data.map(product => ({
          value: product.pid,
          label: product.name
        }))
        setProductOptions(options)
      }
    } catch (error) {
    } finally {
      setLoadingProducts(false)
    }
  }

  useEffect(() => {
    loadStoreProducts()
  }, [])

  // Format field names for user-friendly display
  const formatFieldName = (field) => {
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Handle field edit save
  const handleSaveField = async (field, value) => {
    try {
      const updateData = {
        licenseId: license._id,
        [field]: value,
      }

      const response = await UpdateLicense(updateData)

      if (response.result) {
        showNotification({
          message: `${formatFieldName(field)} updated successfully`,
          className: "bg-admin_primary text-admin_text_inv",
        })
      } else {
        showNotification({
          message: response.error || `Failed to update ${field}`,
          className: "bg-admin_error text-white",
        })
        // Revert the change if API call failed
        setRefreshKey((prev) => prev + 1)
      }
    } catch (error) {
      showNotification({
        message: `An error occurred while updating ${field}`,
        className: "bg-admin_error text-white",
      })
      // Revert the change if API call failed
      setRefreshKey((prev) => prev + 1)
    }
  }


  return (
    <div className="space-y-6">
      {/* License Details and Metadata - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* License Configuration */}
        <div className="bg-admin_paper rounded-xl p-6">
          <Typography 
            component="h2"
            variant="admin_h4" 
            className="mb-4"
          >
            License Configuration
          </Typography>

          <div className="space-y-4">
          {/* Seats - Only show for non-external licenses */}
          {!license.external && (
            <div className="flex items-start justify-between pb-4 border-b border-admin_neutral_1 last:border-b-0">
              <div>
                <Typography
                  variant="admin_label"
                  className="my-0"
                >
                  Seats per License
                </Typography>

                <Typography 
                  variant="admin_txt2_dim"
                  className="block mt-0.5"
                >
                  Maximum number of devices that can use this license
                </Typography>
              </div>

              <div className="text-right">
                <Input
                  variant="admin"
                  type="number"
                  value={license.seats}
                  className="w-[130px]"
                  onChange={(e) => {
                    const newValue = e.target.value
                    // Update license object directly
                    setLicense((prev) => ({
                      ...prev,
                      seats: newValue,
                    }))
                  }}
                  onBlur={(e) => {
                    // Save on blur to avoid too many API calls
                    handleSaveField("seats", e.target.value)
                  }}
                  min={1}
                />

                <Typography
                  variant="admin_txt2_dim"
                  className="mt-1"
                >
                  devices
                </Typography>
              </div>
            </div>
          )}

          {/* Users per License - Only show for non-external licenses */}
          {!license.external && (
            <div className="flex items-start justify-between pb-4 border-b border-admin_neutral_1 last:border-b-0">
              <div>
                <Typography
                  variant="admin_label"
                  className="my-0"
                >
                  Users per License
                </Typography>

                <Typography
                  variant="admin_txt2_dim"
                  className="block mt-0.5"
                >
                  Maximum distinct users that can activate this license. 0 = unlimited (seats limit applies).
                </Typography>
              </div>

              <div className="text-right">
                <Input
                  variant="admin"
                  type="number"
                  value={license.users_per_license ?? 0}
                  className="w-[130px]"
                  onChange={(e) => {
                    const newValue = e.target.value
                    setLicense((prev) => ({
                      ...prev,
                      users_per_license: newValue,
                    }))
                  }}
                  onBlur={(e) => {
                    handleSaveField("users_per_license", e.target.value)
                  }}
                  min={0}
                />

                <Typography
                  variant="admin_txt2_dim"
                  className="mt-1"
                >
                  users (0 = unlimited)
                </Typography>
              </div>
            </div>
          )}

          {/* Product ID */}
          <div className="flex items-start justify-between pb-4 border-b border-admin_neutral_1">
            <div>
              <Typography
                variant="admin_label"
                className="my-0"
              >
                Product
              </Typography>

              <Typography 
                variant="admin_txt2_dim"
                className="block mt-0.5"
              >
                Product that this license activates
              </Typography>
            </div>

            <div className="">
              <DropDownSelect
                variant="admin"
                options={productOptions}
                value={license.productId ? productOptions.filter(opt => opt.value === license.productId) : []}
                onChange={(values) => {
                  const selectedProduct = values?.[0]?.value || ""
                  // Update license object directly
                  setLicense((prev) => ({
                    ...prev,
                    productId: selectedProduct,
                  }))
                  // Save immediately
                  handleSaveField("productId", selectedProduct)
                }}
                placeholder="Select a product..."
                className="w-[200px] xl:w-64"
                searchEnabled
                showCheckbox={false}
                loading={loadingProducts}
              />
            </div>
          </div>

          {/* Supported Platforms */}
          <div className="flex items-start justify-between pb-4 border-b border-admin_neutral_1">
            <div>
              <Typography
                variant="admin_label"
                className="my-0"
              >
                Supported Platforms
              </Typography>

              <Typography 
                variant="admin_txt2_dim"
                className="block mt-0.5"
              >
                Device platforms that can activate this license
              </Typography>
            </div>

            <div className="flex flex-col items-end gap-3">
              <DropDownSelect
                variant="admin"
                options={[
                  { value: "any", label: "All Platforms" },
                  { value: "ios", label: "iOS" },
                  { value: "android", label: "Android" },
                  { value: "windows", label: "Windows" },
                  { value: "linux", label: "Linux" },
                  { value: "macos", label: "macOS" },
                  { value: "web", label: "Web" },
                ]}
                value={(() => {
                  try {
                    const platforms = license.supported_platforms || []

                    // Ensure platforms is an array
                    if (!Array.isArray(platforms)) {
                      return [{ value: "any", label: "All Platforms" }]
                    }

                    if (platforms.length === 0 || platforms.includes("any")) {
                      return [{ value: "any", label: "All Platforms" }]
                    }

                    return platforms
                      .filter(platform => typeof platform === 'string') // Ensure string values
                      .map(platform => ({
                        value: String(platform).toLowerCase(), // Normalize to lowercase
                        label: platform === "ios" ? "iOS" :
                               platform === "android" ? "Android" :
                               platform === "windows" ? "Windows" :
                               platform === "linux" ? "Linux" :
                               platform === "macos" ? "macOS" :
                               platform === "web" ? "Web" :
                               String(platform).charAt(0).toUpperCase() + String(platform).slice(1).toLowerCase()
                      }))
                  } catch (error) {
                    console.error('Error processing supported_platforms:', error)
                    return [{ value: "any", label: "All Platforms" }]
                  }
                })()}
                onChange={(values) => {
                  let selectedPlatforms = values.map(v => v.value)
                  const currentPlatforms = license.supported_platforms || []
                  
                  // Check if user was switching from "any" to specific platforms
                  if ((currentPlatforms.length === 0 || currentPlatforms.includes("any")) && 
                      selectedPlatforms.includes("any") && 
                      selectedPlatforms.length > 1) {
                    // User had empty/any state and clicked on a specific platform
                    // Remove "any" and keep only the specific platforms
                    selectedPlatforms = selectedPlatforms.filter(p => p !== "any")
                  } else if (selectedPlatforms.includes("any") && selectedPlatforms.length > 1) {
                    // User selected "any" along with other platforms, keep only "any"
                    selectedPlatforms = ["any"]
                  }
                  
                  // Update license object directly
                  setLicense((prev) => ({
                    ...prev,
                    supported_platforms: selectedPlatforms,
                  }))
                  // Save immediately
                  handleSaveField("supported_platforms", selectedPlatforms)
                }}
                placeholder="Select platforms..."
                className="w-[200px] xl:w-64"
                multiSelection={true}
                showCheckbox={false}
                onValueItemRender={(item) => {
                  if (!item) return null
                  return (
                    <div className="flex items-center gap-1">
                      {renderPlatformIcon(item.value, "w-3 h-3 text-admin_text_secondary")}
                      <span>{item.label || item.value}</span>
                    </div>
                  )
                }}
                showSelectionTick={true}
                searchEnabled
                onItemRender={(option) => (
                  <div className="flex items-center gap-1">
                    {renderPlatformIcon(option.value, "w-4 h-4 text-admin_text_secondary")}
                    <span>{option.label}</span>
                  </div>
                )}
              />
              
              {/* Selected Platforms Display */}
              <div className="w-full flex items-center justify-end gap-2 flex-wrap">
                {(() => {
                  const platforms = license.supported_platforms || []
                  if (platforms.length === 0 || platforms.includes("any")) {
                    return (
                      <div className="flex items-center gap-1 bg-admin_canvas px-2 py-1 rounded text-sm">
                        {renderPlatformIcon("any", "w-3 h-3 text-admin_text_secondary")}
                        <span className="text-admin_text_secondary">All Platforms</span>
                      </div>
                    )
                  }
                  return platforms.map(platform => (
                    <div key={platform} className="flex items-center gap-1 bg-admin_canvas px-2 py-1 rounded text-sm">
                      {renderPlatformIcon(platform, "w-3 h-3 text-admin_text_secondary")}
                      <span className="text-admin_text_secondary">
                        {platform === "ios" ? "iOS" :
                         platform === "android" ? "Android" :
                         platform === "windows" ? "Windows" :
                         platform === "linux" ? "Linux" :
                         platform === "macos" ? "macOS" :
                         platform === "web" ? "Web" :
                         platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </span>
                    </div>
                  ))
                })()}
              </div>
            </div>
          </div>

          {/* Validity - Only show for non-external licenses */}
          {!license.external && (
            <div className="flex items-start justify-between pb-4 border-b border-admin_neutral_1 last:border-b-0">
              <div>
                <Typography
                  variant="admin_label"
                  className="my-0"
                >
                  Validity Period
                </Typography>

                <Typography 
                  variant="admin_txt2_dim"
                  className="block mt-0.5"
                >
                  How long the license remains valid
                </Typography>
              </div>
              
              <div className="text-right">
                <Input
                  variant="admin"
                  type="number"
                  value={license.validity || 0}
                  className="w-[130px]"
                  onChange={(e) => {
                    const newValue = e.target.value
                    // Update license object directly
                    setLicense((prev) => ({
                      ...prev,
                      validity: newValue,
                    }))
                  }}
                  onBlur={(e) => {
                    // Save on blur to avoid too many API calls
                    handleSaveField("validity", e.target.value)
                  }}
                  min={0}
                  placeholder="365"
                />
                <Typography
                  variant="admin_txt2"
                  className="text-admin_text_secondary mt-1 text-xs"
                >
                  days (0 = lifetime)
                </Typography>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Metadata */}
        <div className="bg-admin_paper rounded-xl p-6">
          <Typography 
            component="h2"
            variant="admin_h4" 
            className="mb-4"
          >
            Metadata
          </Typography>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Column 1 */}
            <div className="space-y-4">
              <div className="pb-4 border-b border-admin_bordered last:border-b-0">
                <Typography
                  variant="admin_label"
                  className="my-0"
                >
                  License ID:
                </Typography>

                <Typography variant="admin_txt1_dim">
                  {license._id}
                </Typography>
              </div>

              <div className="pb-4 border-b border-admin_bordered last:border-b-0">
                <Typography
                  variant="admin_label"
                  className="my-0"
                >
                  License Pack:
                </Typography>

                <button
                  className="text-admin_primary hover:underline"
                  onClick={() => {
                    // Close current license viewer first
                    onClose()
                    // Then navigate to the pack viewer
                    navigate(
                      `/admin/product-licensing/packs/${license.licensePack?._id}`
                    )
                  }}
                >
                  {license.licensePack?.name || "No pack"}
                </button>
              </div>

              <div className="pb-4 border-b border-admin_bordered last:border-b-0">
                <Typography
                  variant="admin_label"
                  className="my-0"
                >
                  License Type:
                </Typography>

                <div className="flex items-center space-x-2">
                  <Typography variant="admin_txt1_dim">
                    {license.external ? "External License" : "Internal License"}
                  </Typography>

                  {license.external && (
                    <Typography 
                      variant="admin_txt2"
                      className="text-white font-medium bg-violet-600 rounded-full px-3 py-1"
                    >
                      external
                    </Typography>
                  )}
                </div>

                {license.external && (
                  <Typography 
                    variant="admin_txt2_dim" 
                    className="block mt-1"
                  >
                    Managed by partner system. Device tracking handled externally.
                  </Typography>
                )}
              </div>
            </div>

            {/* Column 2 */}
            <div className="space-y-4">
              <div className="pb-4 border-b border-admin_bordered last:border-b-0">
                <Typography
                  variant="admin_label"
                  className="my-0"
                >
                  Created Date:
                </Typography>

                <Typography variant="admin_txt1_dim">
                  {new Date(license.created).toLocaleDateString()} at{" "}
                  {new Date(license.created).toLocaleTimeString()}
                </Typography>
              </div>

              <div className="pb-4 border-b border-admin_bordered last:border-b-0">
                <Typography
                  variant="admin_label"
                  className="my-0"
                >
                  Valid Until:
                </Typography>

                <Typography variant="admin_txt1_dim">
                  {license.validUntil
                    ? new Date(license.validUntil).toLocaleDateString()
                    : "Lifetime"}
                </Typography>
              </div>

              {license.subscriptionId && (
                <div className="pb-4 border-b border-admin_bordered last:border-b-0">
                  <Typography
                    variant="admin_label"
                    className="my-0"
                  >
                    Subscription ID:
                  </Typography>

                  <Typography
                    variant="admin_txt1_dim"
                    className="font-mono"
                  >
                    {license.subscriptionId}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}